import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Camera, Star, MapPin, Image, Globe, CheckCircle, Badge as BadgeIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { WorkshopLeaderProfile } from '@/components/workshops/WorkshopLeaderProfile';
import { WorkshopListingGrid } from '@/components/workshops/WorkshopListingGrid';
import { WorkshopStats } from '@/components/workshops/WorkshopStats';
import { getErrorMessage } from '@/lib/errorMessage';
import { redirectToCheckout, stripSessionIdFromUrl, trackPurchase } from '@/lib/analytics';

interface WorkshopLeader {
  id: string;
  user_id: string;
  business_name: string;
  bio: string | null;
  profile_photo_url: string | null;
  website_url: string | null;
  paid_through: string;
  is_active: boolean;
}

export default function WorkshopManage() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [leader, setLeader] = useState<WorkshopLeader | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showAddWorkshop, setShowAddWorkshop] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  const startWorkshopCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-workshop-checkout', {
        body: {},
      });
      if (error) throw error;
      if (data?.url) {
        redirectToCheckout(data.url, 'Workshop Leader');
      } else if (data?.error) {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Handle Stripe redirect — complete workshop setup (run only once)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && user && !setupComplete) {
      setSetupComplete(true); // Prevent duplicate calls
      setLoading(true);
      completeSetup(sessionId);
    }
  }, [searchParams, user, setupComplete]);

  // Load leader profile (skip if session_id is present — completeSetup handles it)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (user && !authLoading && !sessionId) {
      loadLeaderProfile();
    } else if (!user && !authLoading) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const completeSetup = async (sessionId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('complete-workshop-setup', {
        body: { sessionId },
      });
      if (error) throw error;
      if (data?.success) {
        // Never rejects; a tracking failure must not skip activation.
        void trackPurchase(sessionId, 'Workshop Leader', data);
        // Remove session_id from URL so setup doesn't run again on re-render
        // and a later pageview cannot include the Stripe session id.
        stripSessionIdFromUrl();

        toast.success('Workshop listing account activated!');
        if (data.premium_cancelled) {
          toast.info('Your Premium subscription will end at its current billing period. Your Workshop Leader subscription includes all Premium features.');
        }
        // Notify Header to re-check workshop leader status
        window.dispatchEvent(new Event('workshop-leader-activated'));
        loadLeaderProfile();
      }
    } catch (err) {
      console.error('Workshop setup error:', err);
      toast.error('Failed to activate workshop account');
    }
  };

  const loadLeaderProfile = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('workshop_leaders')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setLeader(data);
    } catch (err) {
      console.error('Error loading leader profile:', err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SEOHead
          title="Manage Workshops | Milky Way Planner"
          description="Manage your Milky Way Planner workshop listings. This page is for workshop leaders and is not part of the public workshop directory."
          noindex
        />
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
          <h1 className="text-2xl font-semibold text-foreground">Workshop Manager</h1>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <SEOHead title="Manage Workshops | Milky Way Planner" description="Manage your Milky Way Planner workshop listings. This page is for workshop leaders and is not part of the public workshop directory." noindex />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center space-y-4">
              <Camera className="w-10 h-10 mx-auto text-muted-foreground" />
              <h2 className="text-xl font-semibold">Sign in to manage workshops</h2>
              <p className="text-muted-foreground">You need to be signed in to manage your workshop listings.</p>
              <Button onClick={() => navigate('/workshops/list-your-workshop')}>
                Get Started
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (!leader || !leader.is_active) {
    return (
      <>
        <SEOHead title="Manage Workshops | Milky Way Planner" description="Manage your Milky Way Planner workshop listings. This page is for workshop leaders and is not part of the public workshop directory." noindex />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center space-y-4">
              <Camera className="w-10 h-10 mx-auto text-muted-foreground" />
              <h2 className="text-xl font-semibold">Workshop Leader Access</h2>
              <p className="text-muted-foreground">
                {leader && !leader.is_active
                  ? 'Your workshop listing subscription has expired.'
                  : 'Subscribe to list your astrophotography workshops on Milky Way Planner.'}
              </p>
              <Button onClick={startWorkshopCheckout} variant="premium" disabled={checkoutLoading}>
                {checkoutLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to checkout...</>
                ) : (
                  leader ? 'Renew Subscription' : 'Get Started — $200/year'
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const hasProfile = leader.bio || leader.profile_photo_url || leader.website_url;
  const hasListings = false; // Will be updated by WorkshopListingGrid

  return (
    <>
      <SEOHead title="Manage Workshops | Milky Way Planner" description="Manage your Milky Way Planner workshop listings. This page is for workshop leaders and is not part of the public workshop directory." noindex />
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b bg-gradient-to-r from-background to-muted/30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold text-foreground">Workshop Manager</h1>
                  <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">Active</Badge>
                </div>
                <p className="text-muted-foreground mt-1">
                  Manage your profile and workshop listings
                </p>
              </div>
              {leader.paid_through && (
                <div className="text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-lg">
                  <span className="font-medium text-foreground">Subscription</span>
                  <span className="mx-2">·</span>
                  Active through {new Date(leader.paid_through).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
          {/* Getting Started Guide — show when profile or listings are incomplete */}
          {!hasProfile && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-2">Get the most from your listing</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Complete these steps to make your workshops stand out to photographers browsing Milky Way Planner.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {leader.profile_photo_url ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-50/50 border-emerald-200">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm text-emerald-700">Profile photo uploaded</span>
                        </div>
                      ) : (
                        <button onClick={() => setShowProfileEdit(true)} className="flex items-center gap-3 p-3 rounded-lg border bg-background border-border hover:border-blue-300 hover:bg-blue-50/30 transition-colors text-left cursor-pointer">
                          <Image className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">Upload a profile photo</span>
                        </button>
                      )}
                      {leader.bio ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-50/50 border-emerald-200">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm text-emerald-700">Bio written</span>
                        </div>
                      ) : (
                        <button onClick={() => setShowProfileEdit(true)} className="flex items-center gap-3 p-3 rounded-lg border bg-background border-border hover:border-blue-300 hover:bg-blue-50/30 transition-colors text-left cursor-pointer">
                          <BadgeIcon className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">Write your bio</span>
                        </button>
                      )}
                      {leader.website_url ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border bg-emerald-50/50 border-emerald-200">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm text-emerald-700">Website added</span>
                        </div>
                      ) : (
                        <button onClick={() => setShowProfileEdit(true)} className="flex items-center gap-3 p-3 rounded-lg border bg-background border-border hover:border-blue-300 hover:bg-blue-50/30 transition-colors text-left cursor-pointer">
                          <Globe className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">Add your website</span>
                        </button>
                      )}
                      <button onClick={() => setShowAddWorkshop(true)} className="flex items-center gap-3 p-3 rounded-lg border bg-background border-border hover:border-blue-300 hover:bg-blue-50/30 transition-colors text-left cursor-pointer">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Create your first workshop</span>
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <WorkshopStats leaderId={leader.id} />
          <WorkshopLeaderProfile leader={leader} onUpdate={loadLeaderProfile} startEditing={showProfileEdit} onEditingChange={setShowProfileEdit} />
          <WorkshopListingGrid leaderId={leader.id} startWithEditor={showAddWorkshop} onEditorChange={setShowAddWorkshop} />
        </div>
      </div>
    </>
  );
}
